import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { projectChatsApi, companyChatsApi, allChatsApi } from '@/lib/api';
import { useAuth } from '@/hooks/use-auth';
import type { ProjectChatMessage, CompanyChatMessage } from '@/types';

// ============================================
// PROJECT CHATS
// ============================================

export function useProjectChats(projectId: string) {
  return useQuery({
    queryKey: ['project-chats', projectId],
    queryFn: async () => {
      const { data } = await projectChatsApi.getChats(projectId);
      return data;
    },
    enabled: !!projectId,
  });
}

export function useProjectChatAccess(chatId: string) {
  return useQuery({
    queryKey: ['project-chat-access', chatId],
    queryFn: async () => {
      const { data } = await projectChatsApi.getAccess(chatId);
      return data;
    },
    enabled: !!chatId,
  });
}

export function useProjectChatMessages(chatId: string, enabled = true) {
  return useQuery({
    queryKey: ['project-chat-messages', chatId],
    queryFn: async () => {
      const { data } = await projectChatsApi.getMessages(chatId, { limit: 100 });
      return data;
    },
    enabled: !!chatId && enabled,
    refetchInterval: false, // We use websockets for real-time
  });
}

export function useProjectChatMembers(chatId: string, enabled = true) {
  return useQuery({
    queryKey: ['project-chat-members', chatId],
    queryFn: async () => {
      const { data } = await projectChatsApi.getEffectiveMembers(chatId);
      return data;
    },
    enabled: !!chatId && enabled,
  });
}

export function useSendProjectChatMessage(chatId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (content: string) => {
      const { data } = await projectChatsApi.sendMessage(chatId, content);
      return data;
    },
    onSuccess: (newMessage) => {
      // Add message to cache, checking for duplicates (socket may have already added it)
      queryClient.setQueryData<ProjectChatMessage[]>(
        ['project-chat-messages', chatId],
        (old) => {
          if (old?.some((m) => m.id === newMessage.id)) return old;
          return [...(old || []), newMessage];
        }
      );
    },
  });
}

export function useProjectChatSettings(chatId: string) {
  return useQuery({
    queryKey: ['project-chat-settings', chatId],
    queryFn: async () => {
      const { data } = await projectChatsApi.getSettings(chatId);
      return data;
    },
    enabled: !!chatId,
  });
}

export function useUpdateProjectChatSettings(chatId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: { name?: string }) => {
      const { data: result } = await projectChatsApi.updateSettings(chatId, data);
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project-chat-settings', chatId] });
      queryClient.invalidateQueries({ queryKey: ['project-chats'] });
    },
  });
}

export function useAddProjectChatOverride(chatId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: { userId: string; canRead: boolean; canWrite: boolean }) => {
      const { data: result } = await projectChatsApi.addOverride(chatId, data);
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project-chat-settings', chatId] });
    },
  });
}

export function useUpdateProjectChatOverride(chatId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ userId, data }: { userId: string; data: { canRead?: boolean; canWrite?: boolean } }) => {
      const { data: result } = await projectChatsApi.updateOverride(chatId, userId, data);
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project-chat-settings', chatId] });
    },
  });
}

export function useRemoveProjectChatOverride(chatId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (userId: string) => {
      await projectChatsApi.removeOverride(chatId, userId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project-chat-settings', chatId] });
    },
  });
}

// ============================================
// COMPANY CHATS
// ============================================

export function useCompanyChats() {
  return useQuery({
    queryKey: ['company-chats'],
    queryFn: async () => {
      const { data } = await companyChatsApi.getChats();
      return data;
    },
    staleTime: 0, // Always refetch on mount
    refetchOnWindowFocus: true,
  });
}

export function useCompanyChat(chatId: string) {
  return useQuery({
    queryKey: ['company-chat', chatId],
    queryFn: async () => {
      const { data } = await companyChatsApi.getChat(chatId);
      return data;
    },
    enabled: !!chatId,
  });
}

export function useCreateCompanyChat() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: { name: string; description?: string }) => {
      const { data: result } = await companyChatsApi.createChat(data);
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['company-chats'] });
    },
  });
}

export function useUpdateCompanyChat(chatId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: { name?: string; description?: string }) => {
      const { data: result } = await companyChatsApi.updateChat(chatId, data);
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['company-chats'] });
      queryClient.invalidateQueries({ queryKey: ['company-chat', chatId] });
    },
  });
}

export function useDeleteCompanyChat() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (chatId: string) => {
      await companyChatsApi.deleteChat(chatId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['company-chats'] });
    },
  });
}

export function useCompanyChatAccess(chatId: string) {
  return useQuery({
    queryKey: ['company-chat-access', chatId],
    queryFn: async () => {
      const { data } = await companyChatsApi.getAccess(chatId);
      return data;
    },
    enabled: !!chatId,
  });
}

export function useCompanyChatMessages(chatId: string, enabled = true) {
  return useQuery({
    queryKey: ['company-chat-messages', chatId],
    queryFn: async () => {
      const { data } = await companyChatsApi.getMessages(chatId, { limit: 100 });
      return data;
    },
    enabled: !!chatId && enabled,
    refetchInterval: false, // We use websockets for real-time
  });
}

export function useSendCompanyChatMessage(chatId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (content: string) => {
      const { data } = await companyChatsApi.sendMessage(chatId, content);
      return data;
    },
    onSuccess: (newMessage) => {
      // Add message to cache, checking for duplicates (socket may have already added it)
      queryClient.setQueryData<CompanyChatMessage[]>(
        ['company-chat-messages', chatId],
        (old) => {
          if (old?.some((m) => m.id === newMessage.id)) return old;
          return [...(old || []), newMessage];
        }
      );
    },
  });
}

export function useCompanyChatSettings(chatId: string) {
  return useQuery({
    queryKey: ['company-chat-settings', chatId],
    queryFn: async () => {
      const { data } = await companyChatsApi.getSettings(chatId);
      return data;
    },
    enabled: !!chatId,
  });
}

export function useCompanyChatMembers(chatId: string, enabled = true) {
  return useQuery({
    queryKey: ['company-chat-members', chatId],
    queryFn: async () => {
      const { data } = await companyChatsApi.getSettings(chatId);
      return data.members.map((m: { userId: string; canWrite: boolean; user: { id: string; email: string; displayName: string; avatarUrl: string | null } }) => ({
        id: m.userId,
        email: m.user.email,
        displayName: m.user.displayName,
        avatarUrl: m.user.avatarUrl,
        canWrite: m.canWrite,
      }));
    },
    enabled: !!chatId && enabled,
  });
}

export function useAddCompanyChatMember(chatId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: { userId: string; canWrite?: boolean }) => {
      const { data: result } = await companyChatsApi.addMember(chatId, data);
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['company-chat-settings', chatId] });
      queryClient.invalidateQueries({ queryKey: ['company-chat-members', chatId] });
      queryClient.invalidateQueries({ queryKey: ['company-chats'] });
    },
    onError: (error) => {
      console.error('Failed to add company chat member:', error);
    },
  });
}

export function useUpdateCompanyChatMember(chatId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ userId, data }: { userId: string; data: { canWrite?: boolean } }) => {
      const { data: result } = await companyChatsApi.updateMember(chatId, userId, data);
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['company-chat-settings', chatId] });
    },
  });
}

export function useRemoveCompanyChatMember(chatId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (userId: string) => {
      await companyChatsApi.removeMember(chatId, userId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['company-chat-settings', chatId] });
      queryClient.invalidateQueries({ queryKey: ['company-chat-members', chatId] });
      queryClient.invalidateQueries({ queryKey: ['company-chats'] });
    },
  });
}

export function useJoinCompanyChat() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (chatId: string) => {
      const { data } = await companyChatsApi.join(chatId);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['company-chats'] });
    },
  });
}

export function useLeaveCompanyChat() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (chatId: string) => {
      await companyChatsApi.leave(chatId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['company-chats'] });
    },
  });
}

export function useMarkCompanyChatAsRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (chatId: string) => {
      await companyChatsApi.markAsRead(chatId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['company-chats'] });
      queryClient.invalidateQueries({ queryKey: ['all-chats'] });
    },
  });
}

// ============================================
// UNIFIED CHATS (All chats view)
// ============================================

export function useAllChats() {
  const { isAuthenticated, isLoading } = useAuth();

  return useQuery({
    queryKey: ['all-chats'],
    queryFn: async () => {
      const { data } = await allChatsApi.getAll();
      return data;
    },
    enabled: isAuthenticated && !isLoading,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });
}

export function useMarkProjectChatAsRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (chatId: string) => {
      await allChatsApi.markProjectChatAsRead(chatId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['all-chats'] });
    },
  });
}

export function useFindOrCreatePrivateChat() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (targetUserId: string) => {
      const { data } = await companyChatsApi.findOrCreatePrivateChat(targetUserId);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['all-chats'] });
      queryClient.invalidateQueries({ queryKey: ['company-chats'] });
    },
  });
}
